import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      verifyPayment(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const verifyPayment = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;

    if (attempts >= maxAttempts) {
      setStatus('error');
      toast.error('Payment verification timed out. Please contact support.');
      return;
    }

    try {
      const token = localStorage.getItem('careerboost_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/payments/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Payment confirmed!');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        return;
      }

      // Continue polling with exponential backoff
      const delay = Math.min(2000 * Math.pow(1.5, attempts), 8000);
      setTimeout(() => verifyPayment(sessionId, attempts + 1), delay);
    } catch (error) {
      // Don't retry on 4xx/5xx errors - these are terminal
      if (error.response && error.response.status >= 400) {
        setStatus('error');
        return;
      }
      // Only retry on network errors with exponential backoff
      if (attempts < maxAttempts - 1) {
        const delay = Math.min(2000 * Math.pow(1.5, attempts), 8000);
        setTimeout(() => verifyPayment(sessionId, attempts + 1), delay);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-xl" data-testid="payment-success-card">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'verifying' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-indigo-600 animate-spin mb-6" />
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Verifying Payment...
              </h1>
              <p className="text-slate-600">
                Please wait while we confirm your payment.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Payment Successful!
              </h1>
              <p className="text-slate-600 mb-6">
                Your account has been upgraded. Enjoy all the premium features!
              </p>
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="go-to-dashboard-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Go to Dashboard
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">❌</span>
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Verification Failed
              </h1>
              <p className="text-slate-600 mb-6">
                We couldn't verify your payment. If you were charged, please contact support.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/pricing')}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                >
                  Try Again
                </Button>
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="w-full"
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
